import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";

import { taskPlanName } from "@bfc-software/bfc-web-ui/lib/task-utils/naming";
import { eligibilityOf } from "./eligibility";
import type {
  PickCapture,
  PickExecution,
  PickLine,
  PickStatus,
  PickUnit,
  PlanActivation,
  Task,
  TaskPage,
  TaskPlan,
  TaskStatus,
} from "./types";

/**
 * The seam between the task planner's contract and this screen's.
 *
 * One file on purpose. Anything the screen draws that the service does not state
 * carries a `MISSING:` note naming why - so `grep "MISSING:" wire.ts` is the
 * complete, machine-checked inventory of what is approximated. When the service
 * grows one of them, the note and the fill go together, the compiler names every
 * caller that was leaning on it, and nothing else in `-data` moves.
 *
 * Every vocabulary below is keyed by its generated union, so the compiler rejects
 * both a token the service dropped and a token it added. Everything the screen
 * draws is drawn from the row the listing sent: `TaskActivationSummary` carries
 * each task's own `routes`, its plan's warehouse-scoped number, and both halves of
 * the pick count - so no second read, and nothing derived from a status.
 *
 * The mappers below are the other half: the components never see a wire type, and
 * neither the real reads in `use-plans.ts` nor the fixture behind the MSW handlers
 * ever sees a view type. Which of the two is feeding `toTask`/`toTaskPlan` is not
 * something the screen can tell.
 */

/**
 * Why a plan's activation is nullable rather than a status field.
 *
 * The service stores each plan's tasks once and immutably at plan time, and
 * every mutable fact about execution lives in a second table keyed to it -
 * `plan_activation`, joined to `task_activation` for the tasks it released. A
 * plan nobody has released has no `plan_activation` row at all, which is what
 * `PlanActivation`'s `never` case means, and why it is a union and not a flag.
 *
 * Two worlds read tasks now, and they are not the same world. The All tasks
 * tab still reads the activation listing only - `TaskActivationSummary`,
 * carrying status, holder and both halves of the pick count - because its PM
 * decision stands: no unreleased task appears there. The plans tab is
 * different: every plan on it has tasks to show, whatever its activation
 * state, because the planning worker writes the task rows once at plan time
 * and activation is a second, later fact about them. So an opened plan card
 * reads `GET /{orgId}/task-activations` when the plan is released - the same
 * inner-joined read the All tasks tab makes - and `GET /{orgId}/tasks`
 * otherwise, which answers from the task rows themselves and needs no
 * activation to exist. `usePlanTasks` in `use-plans.ts` is where that branch
 * lives; `WireTaskRow` below is its two-case seam.
 */

type Schemas = TaskPlannerSchemaTypes.components["schemas"];
type Operations = TaskPlannerSchemaTypes.operations;

/** `GET /{orgId}/task-plans`, as its generated operation states its params. */
type PlanListQuery = NonNullable<
  Operations["ListTaskPlans"]["parameters"]["query"]
>;

/* -------------------------------------------------------------------------- */
/* What the wire actually states                                              */
/* -------------------------------------------------------------------------- */

/** `GET /{orgId}/task-plans` - one plan, with its activation rollup. */
export type WireTaskPlan = Schemas["TaskPlanSummary"];

/**
 * A plan's activation rollup, or `null` for a plan nobody has released.
 *
 * Four distinct answers, not a boolean: an activation being processed is not one
 * that finished, and one that failed is not one that never happened.
 */
export type WireActivationState = Schemas["TaskPlanActivationState"];

/** Live task rows of the current activation, counted by status. */
export type WireActivationCounts = Schemas["TaskActivationCounts"];

/**
 * `GET /{orgId}/task-activations` - one assigned unit of work.
 *
 * The only wire shape that carries a task's status and who holds it, which is
 * why it and not `TaskSummary` is what a task row is built from.
 */
export type WireTaskActivation = Schemas["TaskActivationSummary"];

/**
 * `GET /{orgId}/planned-routes` - one route a plan covers, with its plan.
 *
 * The cross-plan counterpart of the `routes` array nested in a plan: it answers
 * "which plan covers route X" without knowing the plan first.
 *
 * Neither tab reads it. Both carry a `routeCode` filter the service applies, and
 * every row states the routes it picks for - so nothing here needs the route
 * table walked. The type is kept because this endpoint is the only cross-plan
 * view of it.
 */
export type WirePlannedRoute = Schemas["PlannedRoute"];

/** Cursor page envelope, shared by every task planner listing that pages. */
export type WirePagination = Schemas["Pagination"];

/** One snapshotted zone, as both listings and the pick detail state it. */
export type WireTaskPlanZone = Schemas["TaskPlanZone"];

/**
 * A plan's or task's zones as a label list, de-duplicated by `zone_ext_id` -
 * which is the whole reason this is a function and not a `.map`: `zones` is one
 * entry per *item snapshot* (`TaskPlanZone.item_snapshot_id`,
 * task-planner.ts:1403), so a plan with forty picks in Dry Grocery states Dry
 * Grocery forty times.
 *
 * The **abbreviation**, uppercased, since SR-342 item Both-2: each zone is
 * configured with a three-letter code and that is what the PM wants a tag to
 * say, so a Freezer task reads FRZ. `zone_abbreviation` is optional on the
 * wire and genuinely absent in practice (`mocks/payloads/task-plans/
 * outbound.ts`'s `ZONE_PACKOUT` omits it), so an absent one falls back to the
 * uppercased `zone_name`: a tag longer than three letters is worse than a
 * short one, but both are far better than a zone that silently vanishes from
 * the row. Uppercasing the fallback too keeps the column one shape rather
 * than mixing "FRZ" with "Packout".
 *
 * The Zones *filter* is untouched by this and stays on the name
 * (`use-zone-catalog.ts`): it queries by `zone_ext_id`, so the label it
 * offers is independent of the label drawn here.
 */
export function zoneLabelsOf(zones: ReadonlyArray<WireTaskPlanZone>): string[] {
  const byExtId = new Map<number, string>();
  zones.forEach((zone) =>
    byExtId.set(
      zone.zone_ext_id,
      (zone.zone_abbreviation ?? zone.zone_name).toUpperCase(),
    ),
  );
  return [...byExtId.values()].sort((a, b) => a.localeCompare(b));
}

/** One snapshotted customer, as both listings state it (task-planner.ts:1550). */
export type WireTaskPlanCustomer = Schemas["TaskPlanCustomerSnapshot"];

/**
 * A plan's or task's customers as a name list, de-duplicated by
 * `customer_ext_id` - the same reason `zoneLabelsOf` is a function and not a
 * `.map`: `customers` is one entry per *snapshot row* (one per stop,
 * `TaskPlanCustomerSnapshot.location_id`, task-planner.ts:1560), so a customer
 * with two stops on the plan is stated twice, each with its own `location_id`.
 *
 * The full `name`, not an abbreviation: unlike a zone there is no short code
 * on the wire. Sorted by name rather than left in the wire's
 * `customer_ext_id` order, so the cell reads alphabetically.
 *
 * `customers` is optional on every row shape (the server always sends it, as
 * an empty array, but the property is not `required`, task-planner.ts:1114),
 * so a call site passes `row.customers ?? []` rather than this helper
 * accepting `undefined`: absence is the caller's fact to decide on, as
 * `zones` is not optional and needs no such line.
 */
export function customerNamesOf(
  customers: ReadonlyArray<WireTaskPlanCustomer>,
): string[] {
  const byExtId = new Map<number, string>();
  customers.forEach((customer) =>
    byExtId.set(customer.customer_ext_id, customer.name),
  );
  return [...byExtId.values()].sort((a, b) => a.localeCompare(b));
}

/* -------------------------------------------------------------------------- */
/* The vocabularies the listings' filters take                                */
/* -------------------------------------------------------------------------- */

/**
 * The named vocabularies below are for values this screen *sends*; values it
 * *reads* stay as the generated unions.
 *
 * Not a style preference. A member is spent into a position annotated with the
 * generated type - `activationQuery`'s return, `SCOPE_STATUSES`' value type -
 * so the schema stays the authority and the name is only a name. Reading is the
 * other direction, and there a bare literal is what a `switch` needs to be
 * exhaustive over - a case written as a named member does not narrow a union of
 * literals, so `statusOf` below would stop noticing a status the service adds.
 * Hence both, deliberately.
 *
 * Each is a `Record<Wire…, Wire…>` rather than an `enum`, and that is the whole
 * point of the shape: an enum is a hand-typed *copy* of literals the generator
 * already emits, and it is checked in one direction only. A value the service
 * renames fails at the member, but a value the service *adds* is silent - the
 * copy just quietly stops covering the vocabulary, and the chip that should have
 * been offered for it is one nobody knows is missing. Keying the record by the
 * generated union makes it exhaustive both ways: a missing key and a stale key
 * are both compile errors, so the schema is the authority on the *set* and not
 * only on each element. The keys being the wire tokens is the price, and it is
 * also the documentation.
 */

/**
 * `activationState` on the plan listing.
 *
 * Five values, of which this screen asks two. Which two, and why the other
 * three are unreachable from the chip row, is at `activationQuery` in
 * `use-plans.ts`.
 */
export type WireActivationStateParam = NonNullable<
  PlanListQuery["activationState"]
>;

export const ActivationStateParam = {
  /** No `plan_activation` row at all: nobody has ever released this plan. */
  none: "none",
  queued: "queued",
  processing: "processing",
  completed: "completed",
  failed: "failed",
} as const satisfies Record<WireActivationStateParam, WireActivationStateParam>;

/**
 * `fulfillmentState` on the plan listing.
 *
 * The chip row's second axis, and the one `activationState` cannot answer: that
 * param filters on the *activation job*, which reaches `completed` as soon as the
 * worker has minted the task rows and says nothing about whether any of them have
 * been picked. This one filters on execution, read off the activation's
 * `execution_started_at`/`execution_completed_at` stamps - the same two fields
 * `activationOf` below carries onto the card, so the chip and the status tag are
 * answering from one source rather than two.
 *
 * Single-valued, which is the constraint the chip row is shaped by: "released and
 * unfinished" is `not_started` ∪ `in_progress` and so is not a request - see
 * `activationQuery` in `use-plans.ts`, where each member is its own chip.
 *
 * The service rejects it alongside `activationState=none` with a 400
 * (`invalid_filter_combination`): a plan with no activation row has no execution
 * state to filter on, so the pair can only describe the empty set. No chip pairs
 * them.
 */
export type WireFulfillmentStateParam = NonNullable<
  PlanListQuery["fulfillmentState"]
>;

export const FulfillmentStateParam = {
  /** Released, and no task under it has been claimed yet. */
  not_started: "not_started",
  /** Released and claimed, with picks still outstanding. */
  in_progress: "in_progress",
  /** Released, and every task in it has reached a terminal state. */
  completed: "completed",
} as const satisfies Record<
  WireFulfillmentStateParam,
  WireFulfillmentStateParam
>;

/**
 * `status` on the activation listing, which is also every row's own status.
 *
 * The wire's `pending` is the unassigned pool; `statusOf` below is where it
 * becomes the word the screen uses.
 */
export type WireTaskActivationStatus = WireTaskActivation["status"];

export const TaskActivationStatus = {
  /** Live, released, and nobody holds it - the unassigned pool. */
  pending: "pending",
  assigned: "assigned",
  claimed: "claimed",
  completed: "completed",
} as const satisfies Record<WireTaskActivationStatus, WireTaskActivationStatus>;

/**
 * The task box's text as the listings' `taskInternalIds` param takes it.
 *
 * The box collects the warehouse-scoped number a selector says out loud, and the
 * param is typed as an integer - so text that is not one names no task at all.
 * `null` rather than a dropped param, because omitting it would read as
 * "unfiltered" on the wire and hand back every task under a search that matched
 * nothing.
 */
export function taskInternalIdParam(text: string): number | null {
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/* -------------------------------------------------------------------------- */
/* A task, from whichever read produced it                                    */
/* -------------------------------------------------------------------------- */

/**
 * `GET /{orgId}/tasks` and `GET /{orgId}/task-plans/{id}/tasks` - a task as
 * the planning worker wrote it, before anybody was asked to pick it.
 */
export type WireTaskSummary = Schemas["TaskSummary"];

/**
 * A task, as whichever read produced it states it.
 *
 * Two cases, one per world: `activated` from the inner-joined activation
 * listing, `planned` from the task rows the worker wrote at plan time. Held as
 * `{ kind; row }` rather than a bare union of the two wire types, because the
 * discriminant is what `toTask` and `statusOf` switch on - a flattened union
 * would leave them guessing which shape they were handed from field presence
 * alone.
 */
export type WireTaskRow =
  | { kind: "activated"; row: WireTaskActivation }
  | { kind: "planned"; row: WireTaskSummary };

/**
 * A plan, as the listing states it - nothing added.
 *
 * The card shows a label and `TaskPlanSummary` carries no name field, but it does
 * not need one: `internal_id` is the warehouse-scoped number a manager reads out,
 * and `lib/task-utils/naming.ts` turns it into the label. The plans half of this seam invents
 * nothing.
 */
export type WirePlanRow = WireTaskPlan;

/* -------------------------------------------------------------------------- */
/* The mapping                                                                */
/* -------------------------------------------------------------------------- */

/**
 * What a task's status reads as, from the activation row.
 *
 * The wire's `pending` is the unassigned pool, which is a different thing from
 * an assigned-but-untouched task - so it does not survive as a status of its
 * own.
 *
 * Takes the activation row directly rather than a `WireTaskRow` - a planned
 * task has no status to map, and a `switch` over the union in here would need
 * an arm for `"planned"` that has no `TaskStatus` to return: exactly the
 * "answerable with a misleading value" shape `Task`'s docblock says the union
 * exists to rule out.
 */
function statusOf(row: WireTaskActivation): TaskStatus {
  switch (row.status) {
    case "pending":
      return "unassigned";
    case "assigned":
      return "assigned";
    case "claimed":
      return "claimed";
    case "completed":
      return "completed";
  }
}

/**
 * A task row, from whichever read produced it.
 *
 * Both arms now name the warehouse-scoped number `task_internal_id` - the
 * shapes used to swap it against `task_id`, which is why this was a switch and
 * not a spread. `TaskActivationSummary.task_id` is still the ULID (now read here, as
 * `taskUlid`) and still unrelated to `TaskSummary.id`, so the arms do not collapse.
 */
export function toTask(source: WireTaskRow): Task {
  switch (source.kind) {
    case "activated":
      return {
        kind: "activated",
        id: String(source.row.task_internal_id),
        taskUlid: source.row.task_id,
        activationId: source.row.activation_id,
        planInternalId: source.row.task_plan_internal_id,
        planActivationId: source.row.plan_activation_id,
        planId: source.row.task_plan_id,
        planName: taskPlanName(source.row.task_plan_internal_id),
        // Task-scoped, not plan-scoped: an optimize-ppt task states the routes
        // its own picks came from, which is a subset of what the plan covers.
        routeCodes: source.row.routes.map((route) => route.route_code),
        zoneNames: zoneLabelsOf(source.row.zones),
        customerNames: customerNamesOf(source.row.customers ?? []),
        selectorId: source.row.selector_id ?? null,
        selectorName: source.row.external_user_id ?? null,
        lineCount: source.row.task_detail_count,
        picksDone: source.row.task_detail_fulfillment_count,
        status: statusOf(source.row),
        startedAt: source.row.claimed_at ?? null,
        eligibility: eligibilityOf(source.row.eligible_user_groups),
      };
    case "planned":
      return {
        kind: "planned",
        id: String(source.row.task_internal_id),
        planId: source.row.task_plan_id,
        planName: taskPlanName(source.row.task_plan_internal_id),
        // `routes` is required and never null on `TaskSummary`, task-scoped,
        // and the same join `TaskActivationSummary.routes` reports - so no
        // fallback and no `MISSING:` note.
        routeCodes: source.row.routes.map((route) => route.route_code),
        zoneNames: zoneLabelsOf(source.row.zones),
        customerNames: customerNamesOf(source.row.customers ?? []),
        lineCount: source.row.detail_count,
      };
  }
}

/**
 * What a plan's activation reads as.
 *
 * Four states rather than a boolean, because the wire genuinely has four and the
 * card renders each differently: a plan nobody released, one whose activation
 * job is still running, one whose job failed, and one whose tasks are on the
 * floor. Collapsing the middle two into "not activated" is what would put an
 * Activate button on a plan that is already activating.
 */
function activationOf(state: WireActivationState | null): PlanActivation {
  if (state === null) {
    return { kind: "never" };
  }

  switch (state.status) {
    case "queued":
    case "processing":
      return { kind: "in-flight", status: state.status };
    case "failed":
      return { kind: "failed" };
    case "completed":
      return {
        kind: "released",
        counts: state.task_activation_counts,
        totalTasks: state.total_tasks,
        // The two execution stamps, straight through. Null is a real answer on
        // both - see `PlanActivation` in `types.ts` for what each null means -
        // so neither is defaulted to a count or to a date.
        executionStartedAt: state.execution_started_at ?? null,
        executionCompletedAt: state.execution_completed_at ?? null,
      };
  }
}

export function toTaskPlan(row: WirePlanRow): TaskPlan {
  return {
    id: row.id,
    internalId: row.internal_id,
    name: taskPlanName(row.internal_id),
    loadBuilderDiagramUrl: row.load_diagram_url,
    createdAt: row.created_at,
    // A queued or processing plan has no routes yet; an empty list is the wire's
    // answer, not a missing one.
    routeCodes: row.routes.map((route) => route.route_code),
    zoneNames: zoneLabelsOf(row.zones),
    customerNames: customerNamesOf(row.customers ?? []),
    plannedTaskCount: row.total_tasks,
    activation: activationOf(row.activation),
  };
}

/**
 * A cursor page, as the screen holds it.
 *
 * `has_more` is the only field every listing populates - the rest are absent
 * rather than zeroed on listings that do not report them, so absence means "not
 * reported" here too.
 */
export function toTaskPage(
  pagination: WirePagination,
  tasks: ReadonlyArray<Task>,
): TaskPage {
  return {
    tasks,
    nextCursor: pagination.has_more ? (pagination.cursor ?? null) : null,
    totalCount: pagination.total_count ?? null,
  };
}

/* -------------------------------------------------------------------------- */
/* The task detail drawer                                                     */
/* -------------------------------------------------------------------------- */

/** `GET /{orgId}/task-plans/{id}/tasks/{taskId}/details` - one pick line. */
export type WireTaskDetailPick = Schemas["TaskDetailPick"];

/** `GET /{orgId}/task-plans/{id}/tasks/{taskId}/history` - the whole task. */
export type WireTaskExecutionHistory = Schemas["TaskExecutionHistory"];

/** One pick's execution record: what was done to it, and how it ended. */
export type WireTaskPickHistory = Schemas["TaskPickHistory"];

/**
 * One device action recorded against a pick - what makes a pick read
 * `in-progress` (`pickStatusOf`). The Activity timeline does not read these:
 * it comes from audit_service (`audit-wire.ts`).
 */
export type WireTaskPickEvent = Schemas["TaskPickEvent"];

/** One captured unit of quantity for one capture requirement kind. */
export type WireTaskPickQuantRecord = Schemas["TaskPickQuantRecord"];

/**
 * What one pick's execution record reads as in the Status column.
 *
 * Two fields, four answers. `fulfillment` is the terminal half and is null
 * until the pick reaches one, so its absence is never itself an outcome - the
 * events decide between the two non-terminal readings: a pick with a device
 * action recorded against it is being worked, and one with none has not been
 * reached. Neither is derived from the *task's* status, deliberately: a claimed
 * task is a statement about the whole task and says nothing about which of its
 * lines the selector has got to.
 *
 * The terminal half is a `switch` rather than a truthiness test on `fulfilled`,
 * so an outcome the service adds fails to compile here instead of quietly
 * reading as a short.
 */
export function pickStatusOf(history: WireTaskPickHistory): PickStatus {
  const fulfillment = history.fulfillment ?? null;

  if (fulfillment === null) {
    return history.events.length > 0 ? "in-progress" : "pending";
  }

  switch (fulfillment.outcome) {
    case "fulfilled":
      return "picked";
    case "shorted":
      return "short";
  }
}

const ABSENT: PickCapture<never> = { kind: "absent" };
const NOT_CONFIRMED: PickCapture<never> = { kind: "not-confirmed" };

/**
 * Groups a pick's `quant_records` by `unit_index` (empty array in ⇒ no units,
 * matching `quant_records`'s "empty when the pick has no catchweight/lot/expiry
 * requirement", §4).
 */
export function toPickUnits(records: WireTaskPickQuantRecord[]): PickUnit[] {
  const byIndex = new Map<number, PickUnit>();
  const at = (i: number): PickUnit => {
    const existing = byIndex.get(i);
    if (existing) return existing;
    const unit: PickUnit = {
      index: i,
      lot: ABSENT,
      expiry: ABSENT,
      catchweight: ABSENT,
    };
    byIndex.set(i, unit);
    return unit;
  };
  for (const r of records) {
    const unit = at(r.unit_index);
    switch (r.kind) {
      case "confirm_lot":
        // lot_code is null EXACTLY when could_not_confirm (§4) — the `!` below trusts that
        // invariant, which the generated type does not enforce structurally.
        unit.lot = r.could_not_confirm
          ? NOT_CONFIRMED
          : { kind: "captured", value: r.lot_code! };
        break;
      case "confirm_expiry":
        unit.expiry = r.could_not_confirm
          ? NOT_CONFIRMED
          : { kind: "captured", value: r.expiration_date! };
        break;
      case "confirm_catchweight":
        // TaskPickCatchweightRecord has no could_not_confirm arm at all — a record's presence
        // always means a captured value.
        unit.catchweight = {
          kind: "captured",
          value: { value: r.weighed_value, uom: r.weight_unit_of_measure },
        };
        break;
    }
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

/** §11 Q1/Q8 verbatim: only a terminal fulfillment carries a picked qty. */
function pickedQuantityOf(
  fulfillment: Schemas["TaskPickFulfillment"] | null,
  plannedQuantity: number,
): number | null {
  if (fulfillment === null) return null; // pending / in-progress: no terminal record yet
  switch (fulfillment.outcome) {
    case "fulfilled":
      return fulfillment.actual_quantity ?? plannedQuantity;
    case "shorted":
      return fulfillment.actual_quantity ?? null;
  }
}

/**
 * The task's execution history, indexed the way the join needs it.
 *
 * `history.activity` is not read: the timeline comes from audit_service.
 *
 * Keyed by `pick_id`, which the schema states *is* `task_detail_pick.id` - the
 * same id `toPickLine` puts on a `PickLine`. That equality is the whole join,
 * and it is worth saying out loud because nothing in either generated type
 * relates the two: the history rows carry item and location fields of their own
 * that look joinable and are not (an item can appear on a task twice - see the
 * two `78957-7` lines in the design's own mock - so nothing but the pick id
 * identifies a line).
 *
 * A `Map` rather than a lookup by scan, because a task's pick count is
 * unbounded and the drawer asks once per line.
 */
export function toTaskHistory(history: WireTaskExecutionHistory): {
  executions: ReadonlyMap<string, PickExecution>;
} {
  const executions = new Map(
    history.picks.map((pick) => [
      pick.pick_id,
      {
        kind: "known" as const,
        status: pickStatusOf(pick),
        pickedQty: pickedQuantityOf(pick.fulfillment ?? null, pick.quantity),
        units: toPickUnits(pick.fulfillment?.quant_records ?? []),
      },
    ]),
  );
  return { executions };
}

/** A pick nothing was reported for. See `PickExecution` in `types.ts`. */
export const UNKNOWN_EXECUTION: PickExecution = { kind: "unknown" };

/**
 * A pick line of a task nobody has released. See `PickExecution` in
 * `types.ts` for why this is not `UNKNOWN_EXECUTION` under another name: the
 * drawer never makes the `/history` read for a planned task (`useTaskDetail`
 * in `use-task-detail.ts` gates it on `task.kind === "activated"`), so every
 * line takes this fallback rather than one produced by a request that ran and
 * came back empty.
 */
export const NOT_RELEASED_EXECUTION: PickExecution = { kind: "not-released" };

/**
 * A pick line, as the drawer draws it.
 *
 * `slot_id` over the deprecated `location` - see the `PickLine` docblock in
 * `types.ts`.
 *
 * `execution` is passed in rather than read off `pick`, because it does not
 * live on `pick` and never will: `TaskDetailPick` is the plan for a line and
 * `ListTaskHistory` is the record of what happened to it, and the service keeps
 * them apart for the same reason `plan_activation` is a second table (see the
 * docblock at the top of this file). A required parameter rather than an
 * optional one, so a caller that has not done the join has to say so with
 * `UNKNOWN_EXECUTION` rather than get "pending" for free.
 */
export function toPickLine(
  pick: WireTaskDetailPick,
  execution: PickExecution,
): PickLine {
  return {
    execution,
    seq: pick.detail_order,
    lineNumber: pick.line_number,
    id: pick.id,
    itemId: pick.item_id,
    itemName: pick.item_name ?? null,
    slotId: pick.slot_id ?? null,
    quantity: pick.quantity,
    uom: pick.uom,
    productType: pick.product_type,
    zoneName: pick.zone?.zone_name ?? null,
    routeCode: pick.route_code,
    routeDate: pick.route_date,
    lpn: pick.lpn ?? null,
    orderNumber: pick.order_number ?? null,
    stopId: pick.stop_id ?? null,
    stopNumber: pick.stop_number ?? null,
    pickSequence: pick.pick_sequence,
    weight: pick.weight ?? null,
    cube: pick.cube ?? null,
  };
}
